import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { RootState, Task } from 'constants/interfaces'
import { Post } from 'redux/reducers/feed'
import { calculateTaskStatus } from 'components/Tasks/utils'
import keys from 'constants/keys'

export const usePersonalViewUsers = (sortedPostsIds: string[]) => {
  const posts = useSelector((s: RootState) => s.feed.posts)
  const isPostsFetching = useSelector((s: RootState) => s.feed.fetching)
  const tasks = useSelector((s: RootState) => s.tasks_v2.tasks, isEqual)
  const ownerTasks = useSelector((s: RootState) => s.tasks_v2.ownerTasks, isEqual)
  const userResults = useSelector((s: RootState) => s.tasks_v2.userResults, isEqual)
  const subtasks = useSelector((s: RootState) => s.tasks_v2.subtasks, isEqual)
  const myUID = useSelector((s: RootState) => s.auth.uid)

  const { undoneTasks, doneTasks } = useMemo(() => {
    const undone: Task[] = []
    const done: Task[] = []

    tasks.forEach((t: Task) => {
      const status = calculateTaskStatus(t, subtasks, userResults)
      const isCompleted =
        status === keys.TASK_STATUSES.DONE.value ||
        status === keys.TASK_STATUSES.APPROVED.value ||
        status === keys.TASK_STATUSES.DISAPPROVED.value ||
        status === keys.TASK_STATUSES.SKIPPED.value ||
        status === keys.TASK_STATUSES.REVIEWED_BY_AI.value
      if (isCompleted) {
        done.push(t)
      } else {
        undone.push(t)
      }
    })

    return { undoneTasks: undone, doneTasks: done }
  }, [subtasks, tasks, userResults])

  const { unreadPosts, readPosts } = useMemo(() => {
    const unread: Post[] = []
    const read: Post[] = []

    posts.forEach((p: Post) => {
      if (sortedPostsIds?.includes(p.post_id)) {
        unread.push(p)
      } else {
        read.push(p)
      }
    })

    return { unreadPosts: unread, readPosts: read }
  }, [posts, sortedPostsIds])

  const aggregatedByUser = useMemo(() => {
    const aggregated: Record<
      string,
      {
        overdueTasks: Task[]
        urgentTasks: Task[]
        uncompletedTasks: Task[]
        posts: Post[]
        completedTasks: Task[]
        readPosts: Post[]
        ownerTasks: Task[]
        isFaded: boolean
      }
    > = {}

    // Add incomplete tasks and categorize them
    for (const t of undoneTasks) {
      const userId = t.created_by
      if (!aggregated[userId]) {
        aggregated[userId] = {
          overdueTasks: [],
          urgentTasks: [],
          uncompletedTasks: [],
          posts: [],
          completedTasks: [],
          readPosts: [],
          ownerTasks: [],
          isFaded: false,
        }
      }

      const hours = t.hours_until_end

      if (t.show_task_bubble === true && hours < 0) {
        // Overdue urgent tasks (show_task_bubble = true AND hours < 0)
        aggregated[userId].overdueTasks.push(t)
      } else if (t.show_task_bubble === true && hours >= 0) {
        // Urgent tasks (show_task_bubble = true AND hours >= 0)
        aggregated[userId].urgentTasks.push(t)
      } else if (t.show_task_bubble === false) {
        // Uncompleted tasks (show_task_bubble = false, not urgent)
        aggregated[userId].uncompletedTasks.push(t)
      }
    }

    // Add completed tasks
    for (const t of doneTasks) {
      const userId = t.created_by
      if (!aggregated[userId]) {
        aggregated[userId] = {
          overdueTasks: [],
          urgentTasks: [],
          uncompletedTasks: [],
          posts: [],
          completedTasks: [],
          readPosts: [],
          ownerTasks: [],
          isFaded: false,
        }
      }
      aggregated[userId].completedTasks.push(t)
    }

    // Add unread posts
    for (const p of unreadPosts) {
      const userId = p.created_by
      if (!aggregated[userId]) {
        aggregated[userId] = {
          overdueTasks: [],
          urgentTasks: [],
          uncompletedTasks: [],
          posts: [],
          completedTasks: [],
          readPosts: [],
          ownerTasks: [],
          isFaded: false,
        }
      }
      aggregated[userId].posts.push(p)
    }

    // Add read posts
    for (const p of readPosts) {
      const userId = p.created_by
      if (!aggregated[userId]) {
        aggregated[userId] = {
          overdueTasks: [],
          urgentTasks: [],
          uncompletedTasks: [],
          posts: [],
          completedTasks: [],
          readPosts: [],
          ownerTasks: [],
          isFaded: false,
        }
      }
      aggregated[userId].readPosts.push(p)
    }

    // Attach owner_tasks to the current user's aggregation (self bubble)
    if (myUID && ownerTasks.length > 0) {
      if (!aggregated[myUID]) {
        aggregated[myUID] = {
          overdueTasks: [],
          urgentTasks: [],
          uncompletedTasks: [],
          posts: [],
          completedTasks: [],
          readPosts: [],
          ownerTasks: [],
          isFaded: false,
        }
      }
      aggregated[myUID].ownerTasks = ownerTasks
    }

    // Set isFaded flag for users with only read posts or only completed tasks
    Object.keys(aggregated).forEach((userId) => {
      const user = aggregated[userId]
      const hasActiveContent = user.overdueTasks.length > 0 || user.urgentTasks.length > 0 || user.posts.length > 0
      const hasOnlyCompletedContent = !hasActiveContent && (user.completedTasks.length > 0 || user.readPosts.length > 0)
      user.isFaded = hasOnlyCompletedContent
    })

    return aggregated
  }, [undoneTasks, doneTasks, unreadPosts, readPosts, ownerTasks, myUID])

  const sortedUsers = useMemo(() => {
    return Object.keys(aggregatedByUser)
      .map((userId) => {
        const { overdueTasks, urgentTasks, posts, readPosts, completedTasks, ownerTasks, isFaded } =
          aggregatedByUser[userId]

        // Self bubble: pin first (category 0), but only when it has content to show.
        // When the current user has no owner tasks, no active todo tasks and no unread posts,
        // fall through so it is either faded (completed/read only) or hidden (no content at all).
        if (userId === myUID) {
          const hasActiveContent =
            ownerTasks.length > 0 || overdueTasks.length > 0 || urgentTasks.length > 0 || posts.length > 0
          if (hasActiveContent) {
            return { userId, category: 0, sortValue: 0, isFaded: false }
          }
        }

        // Category 6: only read posts or completed tasks (with fade effect)
        if (isFaded) {
          // Take the most recent date from read posts or completed tasks
          const latestReadPostDate = readPosts.reduce((latest, post) => {
            return Math.max(latest, post.updated_at_ts || post.created_at_ts || 0)
          }, 0)
          const latestCompletedTaskDate = completedTasks.reduce((latest, task) => {
            return Math.max(latest, task.updated_at_ts || task.created_at_ts || 0)
          }, 0)
          const latestDate = Math.max(latestReadPostDate, latestCompletedTaskDate)
          return { userId, category: 6, sortValue: latestDate, isFaded: true }
        }

        // Category 1: overdue tasks
        if (overdueTasks.length > 0) {
          let minHoursUntilEnd = Infinity
          overdueTasks.forEach((task) => {
            minHoursUntilEnd = Math.min(minHoursUntilEnd, task.hours_until_end)
          })
          return { userId, category: 1, sortValue: minHoursUntilEnd, isFaded: false }
        }

        // Category 2: urgent tasks (show_task_bubble = true)
        if (urgentTasks.length > 0) {
          let minHoursUntilEnd = Infinity
          urgentTasks.forEach((task) => {
            minHoursUntilEnd = Math.min(minHoursUntilEnd, task.hours_until_end)
          })
          return { userId, category: 2, sortValue: minHoursUntilEnd, isFaded: false }
        }

        // Category 3: only unread posts (without overdue/urgent tasks)
        if (posts.length > 0) {
          const latestPostDate = posts.reduce((latest, post) => {
            return Math.max(latest, post.created_at_ts || 0)
          }, 0)
          return { userId, category: 3, sortValue: latestPostDate, isFaded: false }
        }

        // If nothing to show, return null (users with only uncompletedTasks won't be shown)
        return null
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => {
        if (a.category !== b.category) return a.category - b.category

        // Category 1: overdue - sort ascending (most overdue first)
        if (a.category === 1) {
          return a.sortValue - b.sortValue
        }

        // Category 2: urgent - sort ascending (least time first)
        if (a.category === 2) {
          return a.sortValue - b.sortValue
        }

        // Category 3: unread posts only - sort descending (newest first)
        if (a.category === 3) {
          return b.sortValue - a.sortValue
        }

        // Category 6: completed/read - sort descending (latest first)
        if (a.category === 6) {
          return b.sortValue - a.sortValue
        }

        return a.sortValue - b.sortValue
      })
  }, [aggregatedByUser, myUID])

  return {
    sortedUsers,
    aggregatedByUser,
    isPostsFetching,
  }
}
